import { useCallback, useEffect, useRef, useState } from "react";

import {
  searchInstagramAudio,
  type IgAudioTrack,
  type IgAudioType,
  type IgLookupRejectedAccount,
} from "../../../../../api/post";
import {
  refusedEveryAccount,
  rejectedList,
  serverRejectedAccounts,
} from "../InstagramLookupNotice/lookup-rejections";

/**
 * SKE-4542. The Instagram audio catalogue behind the reel audio picker.
 *
 * Two things decide the shape of this hook.
 *
 * Meta searches its two catalogues separately and has no value meaning both, so
 * `audioType` is part of the request rather than a filter over one result set.
 * Switching catalogue is a fresh search, not a re-slice.
 *
 * A track's `downloadUrl` expires in about 36 hours, so it lives here in memory
 * for as long as the composer is open and never reaches form state. Only the
 * `audioId` is written to the post.
 */

export type AudioCatalogState =
  | { kind: "off" }
  | { kind: "loading" }
  | {
      kind: "ready";
      /** Changes with every search, never with a page of the same one. */
      searchId: number;
      query: string;
      tracks: IgAudioTrack[];
      after?: string;
      /** The account `after` belongs to. Pages are fetched from it alone. */
      servedAccountId?: string;
      busy: boolean;
      rejectedAccounts: IgLookupRejectedAccount[];
      loadMoreError?: string;
      /**
       * A page added no track the list did not already hold. Scrolling cannot
       * reveal anything new, so only Show more asks again, until the next search.
       */
      autoPaused: boolean;
      /**
       * Instagram sent no cursor, so the list is complete. Not the same as an
       * absent `after`, which is also dropped when we cannot page from it.
       */
      exhausted: boolean;
    }
  | {
      kind: "unavailable";
      serverMessage?: string;
      rejectedAccounts: IgLookupRejectedAccount[];
      /** Every account asked was refused, so there is nothing left to search. */
      allRejected: boolean;
    };

const serverSentence = (error: unknown): string | undefined => {
  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;

  return typeof message === "string" ? message : undefined;
};

const mergeRejected = (
  earlier: IgLookupRejectedAccount[],
  later: IgLookupRejectedAccount[]
): IgLookupRejectedAccount[] => [
  ...earlier,
  ...later.filter(
    ({ accountId }) => !earlier.some((entry) => entry.accountId === accountId)
  ),
];

/** `3:07`, from Meta's milliseconds. */
export function trackDuration(track: IgAudioTrack): string | undefined {
  const ms = track.durationInMs;
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms <= 0) {
    return undefined;
  }

  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** Who the track is by, which is a different field in each catalogue. */
export function trackAttribution(track: IgAudioTrack): string | undefined {
  return (
    track.displayArtist?.trim() ||
    (track.igUsername?.trim() ? `@${track.igUsername.trim()}` : undefined)
  );
}

export function trackTitle(track: IgAudioTrack): string {
  return track.title?.trim() || "Untitled track";
}

/**
 * Searches one catalogue, and pages through it.
 *
 * No accounts means no call at all: the route needs a publish-capable account,
 * and asking without one answers 403 with a reconnect notice aimed at a customer
 * whose accounts all publish from a phone.
 */
export function useAudioCatalog(accountIds: string[], audioType: IgAudioType) {
  const accountKey = accountIds.join(",");
  const [state, setState] = useState<AudioCatalogState>(() =>
    accountKey ? { kind: "loading" } : { kind: "off" }
  );
  /*
   * Every response checks it is still the latest before writing. A slow first
   * page landing after a fast search would otherwise replace the results the
   * customer is looking at with ones they have already typed past.
   */
  const requestRef = useRef(0);
  /*
   * The request still outstanding, read synchronously. Auto-load can fire after
   * a search has started but before its busy state renders, and paging from the
   * committed state then would supersede the search with the old list.
   */
  const inFlightRef = useRef<number | null>(null);
  const queryRef = useRef("");

  const search = useCallback(
    async (query: string) => {
      queryRef.current = query;

      if (!accountKey) {
        setState({ kind: "off" });
        return;
      }

      const sequence = ++requestRef.current;
      inFlightRef.current = sequence;
      setState((current) =>
        current.kind === "ready"
          ? { ...current, busy: true, loadMoreError: undefined }
          : { kind: "loading" }
      );

      const accountIds = accountKey.split(",");
      try {
        const result = await searchInstagramAudio({
          accountIds,
          audioType,
          query,
        });
        if (sequence !== requestRef.current) return;
        inFlightRef.current = null;

        setState({
          kind: "ready",
          searchId: sequence,
          query,
          tracks: result?.tracks ?? [],
          after: result?.servedAccountId ? result.after : undefined,
          servedAccountId: result?.servedAccountId,
          busy: false,
          rejectedAccounts: rejectedList(result?.rejectedAccounts),
          autoPaused: false,
          exhausted: !result?.after,
        });
      } catch (error) {
        if (sequence !== requestRef.current) return;
        inFlightRef.current = null;
        const refused = serverRejectedAccounts(error);
        setState({
          kind: "unavailable",
          serverMessage: serverSentence(error),
          rejectedAccounts: refused,
          allRejected: refusedEveryAccount(error, accountIds, refused),
        });
      }
    },
    [accountKey, audioType]
  );

  const loadMore = useCallback(async () => {
    if (
      state.kind !== "ready" ||
      !state.after ||
      !state.servedAccountId ||
      state.busy ||
      inFlightRef.current !== null
    ) {
      return;
    }

    const sequence = ++requestRef.current;
    inFlightRef.current = sequence;
    const {
      searchId,
      query,
      after,
      servedAccountId,
      tracks,
      rejectedAccounts,
      autoPaused,
    } = state;
    setState({ ...state, busy: true, loadMoreError: undefined });
    const accountIds = [servedAccountId];

    try {
      const result = await searchInstagramAudio({
        accountIds,
        audioType,
        query,
        after,
      });
      if (sequence !== requestRef.current) return;
      inFlightRef.current = null;

      /*
       * A page can repeat a track an earlier page already carried, and each row
       * is keyed on the id, so appending blindly leaves duplicate keys and the
       * orphaned rows they produce. The server drops repeats WITHIN a page; only
       * the caller can see across them.
       */
      const known = new Set(tracks.map((track) => track.audioId));
      const fresh = (result?.tracks ?? []).filter(
        (track) => !known.has(track.audioId)
      );

      const nextAfter = result?.servedAccountId ? result.after : undefined;

      setState({
        kind: "ready",
        searchId,
        query,
        tracks: [...tracks, ...fresh],
        after: nextAfter,
        servedAccountId: result?.servedAccountId,
        busy: false,
        rejectedAccounts: mergeRejected(
          rejectedAccounts,
          rejectedList(result?.rejectedAccounts)
        ),
        exhausted: !result?.after,
        autoPaused:
          autoPaused ||
          (Boolean(nextAfter) &&
            !fresh.some((track) => Boolean(track.audioId))),
      });
    } catch (error) {
      if (sequence !== requestRef.current) return;
      inFlightRef.current = null;

      const refused = serverRejectedAccounts(error);
      if (refusedEveryAccount(error, accountIds, refused)) {
        setState({
          kind: "unavailable",
          serverMessage: serverSentence(error),
          rejectedAccounts: mergeRejected(rejectedAccounts, refused),
          allRejected: true,
        });
        return;
      }

      setState({
        kind: "ready",
        searchId,
        query,
        tracks,
        after,
        servedAccountId,
        busy: false,
        rejectedAccounts,
        autoPaused,
        exhausted: false,
        loadMoreError: serverSentence(error) ?? "We couldn't load more tracks.",
      });
    }
  }, [accountKey, audioType, state]);

  /*
   * Re-runs on a catalogue switch carrying the query the customer already typed.
   * The two catalogues are separate searches at Meta, so switching is a fresh
   * request, and starting it from an empty query would throw away their words.
   */
  useEffect(() => {
    search(queryRef.current);
  }, [search]);

  return { state, search, loadMore };
}
